import OtaClient from '@crowdin/ota-client';
import { BackendModule, ReadCallback, ResourceKey } from 'i18next';

import { CROWDIN_DISTRIBUTION_HASH, I18N_CACHE_TTL_MS, I18N_LOCALSTORAGE_PREFIX } from './config';

type TCacheEntry = {
  data: ResourceKey;
  timestamp: number;
};

type TMemoryCacheEntry = {
  data: ResourceKey;
  timestamp: number;
};

type TPendingRequest = {
  promise: Promise<ResourceKey>;
  callbacks: ReadCallback[];
};

const memoryCache: Map<string, TMemoryCacheEntry> = new Map();
const pendingRequests: Map<string, TPendingRequest> = new Map();

let otaClient: OtaClient | null = null;

const getOtaClient = (): OtaClient => {
  if (!otaClient) {
    otaClient = new OtaClient(CROWDIN_DISTRIBUTION_HASH);
  }
  return otaClient;
};

const getCacheKey = (language: string, namespace: string): string => {
  return `${I18N_LOCALSTORAGE_PREFIX}${language}_${namespace}`;
};

const getFromMemoryCache = (cacheKey: string): ResourceKey | null => {
  const cached = memoryCache.get(cacheKey);
  if (!cached) return null;

  const isExpired = Date.now() - cached.timestamp > I18N_CACHE_TTL_MS;
  if (isExpired) {
    memoryCache.delete(cacheKey);
    return null;
  }

  return cached.data;
};

const saveToMemoryCache = (cacheKey: string, data: ResourceKey): void => {
  memoryCache.set(cacheKey, {
    data,
    timestamp: Date.now(),
  });
};

const getFromLocalStorage = (cacheKey: string): ResourceKey | null => {
  try {
    const cached = localStorage.getItem(cacheKey);
    if (!cached) return null;

    const entry: TCacheEntry = JSON.parse(cached);
    const isExpired = Date.now() - entry.timestamp > I18N_CACHE_TTL_MS;

    if (isExpired) {
      localStorage.removeItem(cacheKey);
      return null;
    }

    return entry.data;
  } catch {
    return null;
  }
};

const saveToLocalStorage = (cacheKey: string, data: ResourceKey): void => {
  try {
    const entry: TCacheEntry = {
      data,
      timestamp: Date.now(),
    };
    localStorage.setItem(cacheKey, JSON.stringify(entry));
  } catch {
    // localStorage might be full or disabled
  }
};

const loadFromStaticFile = async (language: string, namespace: string): Promise<ResourceKey> => {
  const response = await fetch(`/locales/${language}/${namespace}.json`);
  if (!response.ok) {
    throw new Error(`Failed to load static file: ${response.status}`);
  }
  return response.json() as Promise<ResourceKey>;
};

const loadFromOta = async (language: string, namespace: string): Promise<ResourceKey> => {
  // English is the source language and not included in Crowdin distribution
  if (language === 'en') {
    return loadFromStaticFile(language, namespace);
  }

  const client = getOtaClient();
  // Crowdin manifest.content uses paths like /content/{lang}/{file}.json
  const filePath = `/content/${language}/${namespace}.json`;
  const content = await client.getFileTranslations(filePath);
  if (content === null) {
    throw new Error(`No translations found for ${filePath}`);
  }
  if (typeof content === 'string') {
    return JSON.parse(content) as ResourceKey;
  }
  return content as ResourceKey;
};

export const CrowdinOtaBackend: BackendModule = {
  type: 'backend',

  init(): void {
    // No initialization needed
  },

  read(language: string, namespace: string, callback: ReadCallback): void {
    const cacheKey = getCacheKey(language, namespace);

    // 1. Check memory cache (with TTL)
    const memoryCached = getFromMemoryCache(cacheKey);
    if (memoryCached) {
      callback(null, memoryCached);
      return;
    }

    // 2. Check localStorage cache
    const localStorageCached = getFromLocalStorage(cacheKey);
    if (localStorageCached) {
      saveToMemoryCache(cacheKey, localStorageCached);
      callback(null, localStorageCached);
      return;
    }

    // 3. Check if there's already a pending request for this resource
    const pending = pendingRequests.get(cacheKey);
    if (pending) {
      pending.callbacks.push(callback);
      return;
    }

    // 4. Create new request with deduplication
    const loadPromise = loadFromOta(language, namespace)
      .catch(() => {
        // Fallback to static files on OTA error
        return loadFromStaticFile(language, namespace);
      })
      .then((data) => {
        saveToMemoryCache(cacheKey, data);
        saveToLocalStorage(cacheKey, data);
        return data;
      });

    pendingRequests.set(cacheKey, {
      promise: loadPromise,
      callbacks: [callback],
    });

    loadPromise
      .then((data) => {
        const req = pendingRequests.get(cacheKey);
        if (req) {
          req.callbacks.forEach((cb) => cb(null, data));
        }
      })
      .catch((error) => {
        const req = pendingRequests.get(cacheKey);
        if (req) {
          req.callbacks.forEach((cb) => cb(error, false));
        }
      })
      .finally(() => {
        pendingRequests.delete(cacheKey);
      });
  },
};
